import { isString } from "@swiggy-private/js-utils";
import {
    SwgyHeadersList,
    SwgyHeadersKeys,
    SwgyHeaderKeysList,
    API_ANALYTICS_PLATFORM,
} from "@swiggy-private/checkout-types";
import { MARKETPLACE_BUSINESS_LINE, MARKETPLACE_CATEGORY, MARKETPLACE_ID } from "@swiggy-private/payment-types";

export type Headers = { [header: string]: string | string[] | undefined };
export type SwgyHeaders = { [header: string]: string };

export function isWebPlatform(platform: string): boolean {
    const _platform = platform.toLowerCase();
    return _platform === "mweb" || _platform === "dweb";
}

export function isiOSPlatform(platform: string): boolean {
    const _platform = platform.toLowerCase();
    return _platform === "swiggy-ios" || _platform === "ios";
}

export function isAndroidPlatform(platform: string): boolean {
    const _platform = platform.toLowerCase();
    return _platform === "swiggy-android" || _platform === "an";
}

export const getMarketplaceBusinessLine = (headers: Headers): string => {
    const businessLine = headers["marketplacebusinessline"] as string;
    if (businessLine) return businessLine.toLowerCase();
    return MARKETPLACE_BUSINESS_LINE.REGULAR;
};

export const getMarketplaceCategory = (headers: Headers): string => {
    const businessCategory = headers["marketplacecategory"] as string;
    if (businessCategory) return businessCategory.toLowerCase();
    return "";
};

export const isInstamartFlow = (headers: Headers): boolean => {
    const marketplaceBusinessLine = getMarketplaceBusinessLine(headers);
    const marketplaceCategory = getMarketplaceCategory(headers);
    return (
        marketplaceBusinessLine === MARKETPLACE_BUSINESS_LINE.DASH &&
        marketplaceCategory === MARKETPLACE_CATEGORY.INSTAMART
    );
};

export const isLiquorFlow = (headers: Headers): boolean => {
    const marketplaceBusinessLine = getMarketplaceBusinessLine(headers);
    const marketplaceCategory = getMarketplaceCategory(headers);
    return (
        marketplaceBusinessLine === MARKETPLACE_BUSINESS_LINE.DASH &&
        marketplaceCategory === MARKETPLACE_CATEGORY.STORES
    );
};

export const isJustFlow = (headers: Headers): boolean => {
    const marketplaceBusinessLine = getMarketplaceBusinessLine(headers)?.toLowerCase();
    const marketplaceCategory = getMarketplaceCategory(headers)?.toLowerCase();
    return (
        marketplaceBusinessLine === MARKETPLACE_BUSINESS_LINE.JUST && marketplaceCategory === MARKETPLACE_CATEGORY.JUST
    );
};

export const isGiftingFlow = (headers: Headers): boolean => {
    const marketplaceBusinessLine = getMarketplaceBusinessLine(headers)?.toLowerCase();
    return marketplaceBusinessLine === MARKETPLACE_BUSINESS_LINE.GIFTING;
};

export const isDineoutFlow = (headers: Headers): boolean => {
    const marketplaceBusinessLine = getMarketplaceBusinessLine(headers)?.toLowerCase();
    return marketplaceBusinessLine === MARKETPLACE_BUSINESS_LINE.DINEOUT;
};

export const isAssureFlow = (headers: Headers): boolean => {
    const marketplaceBusinessLine = getMarketplaceBusinessLine(headers)?.toLowerCase();
    return marketplaceBusinessLine === MARKETPLACE_BUSINESS_LINE.ASSURE;
};

/**
 * Unified implementation of getSwiggyHeaders that combines features from all existing implementations.
 * This function processes headers for Swiggy API calls, handling various platform-specific requirements
 * and ensuring proper header formatting.
 *
 * @param headers - Input headers object containing various header values
 * @returns Processed SwgyHeaders object with standardized header values
 */
export function getSwiggyHeaders(headers: Headers = {}): SwgyHeaders {
    const swgyHeaders: SwgyHeaders = {};

    // Process valid Swiggy headers
    Object.entries(headers).forEach(([key, value]) => {
        if (
            isString(value) &&
            value.length > 0 &&
            (SwgyHeadersList.indexOf(key.toLowerCase() as SwgyHeadersKeys) !== -1 || key.toLowerCase() === "tenant")
        ) {
            swgyHeaders[key] = value;
        }
    });

    // Handle location headers
    if (swgyHeaders.lat) {
        swgyHeaders["latitude"] = swgyHeaders.lat;
    }
    if (swgyHeaders.lng) {
        swgyHeaders["longitude"] = swgyHeaders.lng;
    }

    // Handle marketplace ID
    if (headers["m_id"]) {
        swgyHeaders["m_id"] = String(headers["m_id"]);
    } else {
        swgyHeaders["m_id"] = MARKETPLACE_ID.CHECKOUT;

        if (isInstamartFlow(headers)) {
            swgyHeaders["m_id"] = MARKETPLACE_ID.DASH;
        } else if (isGiftingFlow(headers)) {
            swgyHeaders["m_id"] = MARKETPLACE_ID.GIFTING;
        } else if (isDineoutFlow(headers)) {
            swgyHeaders["m_id"] = MARKETPLACE_ID.DINEOUT;
        } else if (isAssureFlow(headers)) {
            swgyHeaders["m_id"] = MARKETPLACE_ID.ASSURE;
        } else if (isLiquorFlow(headers)) {
            swgyHeaders["m_id"] = MARKETPLACE_ID.LIQUOR;
        } else if (isJustFlow(headers)) {
            swgyHeaders["m_id"] = MARKETPLACE_ID.JUST;
        }
        // checkout in api-server, checkout-core
    }

    // Handle payment amount
    if (headers["paymentamount"]) {
        swgyHeaders["paymentamount"] = String(headers["paymentamount"]);
    }

    // Set client IDs
    swgyHeaders["x-client-id"] = "web-payment";
    if (isString(swgyHeaders.platform) && swgyHeaders.platform.length > 0) {
        swgyHeaders["client-id"] = swgyHeaders.platform;
    } else {
        swgyHeaders["client-id"] = "web-payment";
    }

    // Handle platform-specific headers
    const platform = swgyHeaders[SwgyHeaderKeysList.platform] || "";
    switch (platform.toLowerCase()) {
        case "swiggy-android":
        case "an":
            swgyHeaders["x-checkout-webview"] = API_ANALYTICS_PLATFORM.ANDROID;
            swgyHeaders["x-origin-id"] = API_ANALYTICS_PLATFORM.ANDROID;
            break;
        case "swiggy-ios":
        case "ios":
            swgyHeaders["x-checkout-webview"] = API_ANALYTICS_PLATFORM.IOS;
            swgyHeaders["x-origin-id"] = API_ANALYTICS_PLATFORM.IOS;
            break;
        case "swiggy-dweb":
        case "dweb":
            swgyHeaders["x-checkout-webview"] = API_ANALYTICS_PLATFORM.DWEB;
            swgyHeaders["x-origin-id"] = API_ANALYTICS_PLATFORM.DWEB;
            break;
        default:
            swgyHeaders["x-checkout-webview"] = API_ANALYTICS_PLATFORM.MWEB;
            swgyHeaders["x-origin-id"] = API_ANALYTICS_PLATFORM.MWEB;
            break;
    }

    // Handle forwarded IP address for fraud detection for Simpl
    if (headers && headers["x-forwarded-for"] && typeof headers["x-forwarded-for"] === "string") {
        swgyHeaders["X-Forwarded-For"] = headers["x-forwarded-for"];
    }

    // Handle user agent for fraud detection
    if (headers && headers["user-agent"] && typeof headers["user-agent"] === "string") {
        swgyHeaders["browser-user-agent"] = headers["user-agent"];
    }

    // Handle service line
    if (headers && headers["service-line"] && typeof headers["service-line"] === "string") {
        swgyHeaders["service-line"] = headers["service-line"];
    }

    return swgyHeaders;
}
